import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from './routes';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import LoadingScreen from '@/components/ui/LoadingScreen';

// Lazy loading de toutes les pages — code splitting automatique
const LanguageSelection = lazy(() => import('@/pages/onboarding/LanguageSelection'));
const Login            = lazy(() => import('@/pages/auth/Login'));
const Register         = lazy(() => import('@/pages/auth/Register'));
const RegisterForm     = lazy(() => import('@/pages/auth/RegisterForm'));
const EmailVerification = lazy(() => import('@/pages/auth/EmailVerification'));
const Terms            = lazy(() => import('@/pages/auth/Terms'));
const Home             = lazy(() => import('@/pages/home/Home'));
const Messages         = lazy(() => import('@/pages/messages/Messages'));
const Chat             = lazy(() => import('@/pages/messages/Chat'));
const NewPost          = lazy(() => import('@/pages/post/NewPost'));
const PostView         = lazy(() => import('@/pages/post/PostView'));
const EditPost         = lazy(() => import('@/pages/post/EditPost'));
const Search           = lazy(() => import('@/pages/search/Search'));
const Profile          = lazy(() => import('@/pages/profile/Profile'));
const MyProfile        = lazy(() => import('@/pages/profile/Profile'));
const Settings         = lazy(() => import('@/pages/settings/Settings'));
const Notifications    = lazy(() => import('@/pages/notifications/Notifications'));

const wrap = (element: React.ReactNode) => (
  <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
);

export const router = createBrowserRouter([
  // Onboarding
  { path: ROUTES.LANGUAGE, element: wrap(<LanguageSelection />) },

  // Routes publiques (non connecté)
  {
    element: <PublicRoute />,
    children: [
      { path: ROUTES.AUTH,          element: wrap(<Login />) },
      { path: ROUTES.REGISTER,      element: wrap(<Register />) },
      { path: ROUTES.REGISTER_TYPE, element: wrap(<RegisterForm />) },
    ],
  },

  // Route accessible à tous (connecté ou non)
  { path: ROUTES.TERMS, element: wrap(<Terms />) },

  // Email verify (connecté mais non vérifié)
  { path: ROUTES.EMAIL_VERIFY, element: wrap(<EmailVerification />) },

  // Routes privées (connecté + email vérifié)
  {
    element: <PrivateRoute />,
    children: [
      { path: ROUTES.HOME,          element: wrap(<Home />) },
      { path: ROUTES.MESSAGES,      element: wrap(<Messages />) },
      { path: ROUTES.CHAT,          element: wrap(<Chat />) },
      { path: ROUTES.NEW_POST,      element: wrap(<NewPost />) },
      { path: ROUTES.POST,          element: wrap(<PostView />) },
      { path: ROUTES.EDIT_POST,     element: wrap(<EditPost />) },
      { path: ROUTES.SEARCH,        element: wrap(<Search />) },
      { path: ROUTES.PROFILE,       element: wrap(<Profile />) },
      { path: ROUTES.MY_PROFILE,    element: wrap(<MyProfile />) },
      { path: ROUTES.SETTINGS,      element: wrap(<Settings />) },
      { path: ROUTES.NOTIFICATIONS, element: wrap(<Notifications />) },
    ],
  },

  // Fallback
  { path: '/', element: <Navigate to={ROUTES.AUTH} replace /> },
  { path: '*', element: <Navigate to={ROUTES.HOME} replace /> },
]);
